/**
 * The portrait bottom sheet, shell half (portrait plan v0.14.0 P6–P8): the owner of the sheet's UI state
 * (the `SheetApi` handed to the view as `StyleContext.sheet`; core/layout/sheet.ts) and a transparent DOM layer
 * over the sheet below its transport row:
 *
 * - a vertical drag that starts on the handle (the peek rows, or the tab bar) snaps the sheet to a state;
 * - a tap on the handle cycles peek → half → full → peek, unless a tab is under the finger;
 * - a vertical drag on the list scrolls it (the view clamps the scroll to its content);
 * - every other tap goes to the view's hit regions (follow a TOWER row, seek to a FEED row).
 *
 * The layer is `touch-action: none` (the lists are HUD-drawn, so their scroll is ours, not the browser's)
 * and is hidden in landscape. None of this state goes in the deep link.
 */
import type { Rect, StyleLayout } from '../core/api.ts';
import { cycleSheet, snapSheet, type SheetApi, type SheetState, type SheetTab } from '../core/layout/sheet.ts';
import type { StageLayout } from './stage.ts';

export interface SheetHooks {
  /** the state changed: lay the view out again */
  relayout(): void;
  /** the tab or the scroll changed: draw again */
  redraw(): void;
  /** a tap at a stage point: the HUD hit regions; true when one took it */
  tap(x: number, y: number): boolean;
}

/** CSS px a pointer moves before a press counts as a drag */
const DRAG_SLOP = 8;

export class SheetController implements SheetApi {
  readonly el: HTMLDivElement;
  private st: SheetState = 'peek';
  private tb: SheetTab = 'tower';
  private sc = 0;
  private k = 0;
  private sheet: NonNullable<StyleLayout['sheet']> | null = null;
  /** stage px per CSS px, and the stage's CSS origin */
  private toStage = 1;
  private origin: [number, number] = [0, 0];
  private press: { id: number; x: number; y: number; grip: boolean; scroll0: number; drag: boolean } | null = null;

  constructor(private readonly hooks: SheetHooks) {
    this.el = document.createElement('div');
    this.el.id = 'sheet-touch';
    this.el.hidden = true;
    this.el.addEventListener('pointerdown', (e) => { this.down(e); });
    this.el.addEventListener('pointermove', (e) => { this.move(e); });
    this.el.addEventListener('pointerup', (e) => { this.up(e, false); });
    this.el.addEventListener('pointercancel', (e) => { this.up(e, true); });
    this.el.addEventListener('contextmenu', (e) => { e.preventDefault(); });
  }

  get state(): SheetState {
    return this.st;
  }

  get tab(): SheetTab {
    return this.tb;
  }

  get scroll(): number {
    return this.sc;
  }

  get key(): number {
    return this.k;
  }

  setTab(t: SheetTab): void {
    if (t === this.tb) return;
    this.setTabQuiet(t);
    this.changed();
  }

  open(s: SheetState, t: SheetTab): void {
    this.setTabQuiet(t);
    // never shrinks a sheet the viewer opened further
    this.setState(s === 'full' || this.st === 'full' ? 'full' : s === 'half' || this.st === 'half' ? 'half' : 'peek');
    this.changed();
  }

  clampScroll(max: number): void {
    const c = Math.min(Math.max(this.sc, 0), Math.max(0, max));
    if (c !== this.sc) {
      this.sc = c;
      this.k++;
    }
  }

  /** Lay the layer over the sheet below its transport row (`P_TRANSPORT_H` = grip.y − rect.y), or hide it (landscape). */
  place(L: StyleLayout | null, l: StageLayout): void {
    const sh = L?.sheet ?? null;
    this.sheet = sh;
    this.press = null;
    this.el.hidden = !sh;
    if (!sh) return;
    const r = l.resolved;
    const css = r.f / l.dpr;
    this.toStage = 1 / css;
    this.origin = [r.offset.x / l.dpr, r.offset.y / l.dpr];
    const s = this.el.style;
    s.left = `${String(this.origin[0] + sh.grip.x * css)}px`;
    s.top = `${String(this.origin[1] + sh.grip.y * css)}px`;
    s.width = `${String(sh.rect.w * css)}px`;
    s.height = `${String((sh.rect.y + sh.rect.h - sh.grip.y) * css)}px`;
  }

  /** Back to the peek on TOWER, unscrolled (every race mount: nothing of the old sheet carries over). */
  reset(): void {
    this.press = null;
    this.st = 'peek';
    this.tb = 'tower';
    this.sc = 0;
    this.k++;
  }

  private setTabQuiet(t: SheetTab): void {
    if (t !== this.tb) {
      this.tb = t;
      this.sc = 0;
    }
  }

  private setState(s: SheetState): void {
    if (s === this.st) return;
    this.st = s;
    this.press = null;
    this.k++;
    this.hooks.relayout();
  }

  private changed(): void {
    this.k++;
    this.hooks.redraw();
  }

  private stagePt(e: PointerEvent): [number, number] {
    return [(e.clientX - this.origin[0]) * this.toStage, (e.clientY - this.origin[1]) * this.toStage];
  }

  private down(e: PointerEvent): void {
    const sh = this.sheet;
    if (!sh || this.press) return;
    const [x, y] = this.stagePt(e);
    this.press = { id: e.pointerId, x: e.clientX, y: e.clientY, grip: inside(sh.grip, x, y), scroll0: this.sc, drag: false };
    this.el.setPointerCapture(e.pointerId);
    e.preventDefault();
  }

  private move(e: PointerEvent): void {
    const p = this.press;
    if (!p || p.id !== e.pointerId) return;
    const dy = e.clientY - p.y;
    if (!p.drag && Math.abs(dy) > DRAG_SLOP) p.drag = true;
    if (p.drag && !p.grip) {
      this.sc = p.scroll0 - dy * this.toStage; // the view clamps it next frame
      this.changed();
    }
  }

  private up(e: PointerEvent, cancel: boolean): void {
    const p = this.press;
    const sh = this.sheet;
    if (!p || p.id !== e.pointerId) return;
    this.press = null;
    if (this.el.hasPointerCapture(e.pointerId)) this.el.releasePointerCapture(e.pointerId);
    if (cancel || !sh) return;
    const dy = (e.clientY - p.y) * this.toStage;
    if (p.drag) {
      if (p.grip) this.setState(snapSheet(this.st, dy, sh.tops));
      return;
    }
    const [x, y] = this.stagePt(e);
    if (!this.hooks.tap(x, y) && p.grip) this.setState(cycleSheet(this.st));
  }
}

function inside(r: Rect, x: number, y: number): boolean {
  return x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h;
}
